import type { SearchRow } from '@bloo-team/api-client'

/**
 * Result export — the fetched rows as a file.
 *
 * Values are exported RAW, not through formatCell: an export feeds another
 * tool (a spreadsheet, a script, a ticket), and that tool wants the epoch
 * millisecond and the unparsed JSON string, not this UI's presentation of
 * them. The one exception is null/undefined, which becomes an empty cell —
 * "—" is a rendering, not a value.
 */

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  const s =
    typeof value === 'string'
      ? value
      : typeof value === 'boolean' || typeof value === 'number'
        ? String(value)
        : JSON.stringify(value)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** RFC-4180 style: comma separated, CRLF lines, quotes doubled inside quotes. */
export function toCsv(columns: string[], rows: SearchRow[]): string {
  const lines = [columns.map(csvCell).join(',')]
  for (const row of rows) lines.push(columns.map((c) => csvCell(row[c])).join(','))
  return lines.join('\r\n')
}

/** The same rows as JSON — only the named columns, in column order. */
export function toJsonExport(columns: string[], rows: SearchRow[]): string {
  return JSON.stringify(
    rows.map((row) => Object.fromEntries(columns.map((c) => [c, row[c] ?? null]))),
    null,
    2,
  )
}

/** Hand the browser a file to save. */
export function downloadText(filename: string, mime: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** `search-2026-08-21-142530.csv` — sortable, no characters a filesystem minds. */
export function exportFilename(ext: string, now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const stamp =
    `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}` +
    `-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`
  return `search-${stamp}.${ext}`
}
